import React, { useMemo } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import ReactTable from "../../../components/common/ReactTable";

const formatCount = (value) => ((value || 0) === 0 ? "-" : value);

const formatPaddedCount = (value) =>
  (value || 0) === 0 ? "-" : String(value || 0).padStart(2, "0");

const getGroupParticipantCount = (member) => {
  const regionCount = Number(String(member?.region || "").match(/\d+/)?.[0]);

  return (
    member?.participantIds?.length ||
    member?.participants?.length ||
    (Number.isFinite(regionCount) ? regionCount : 0)
  );
};

const getMemberSectionLabel = (participant) =>
  participant?.committeeSection || participant?.committeeType || participant?.committeeName || "";

const sameMemberSet = (left = [], right = []) => {
  const leftSet = new Set(left.filter(Boolean));
  const rightSet = new Set(right.filter(Boolean));

  return (
    leftSet.size > 0 &&
    leftSet.size === rightSet.size &&
    [...leftSet].every((id) => rightSet.has(id))
  );
};

const getParticipantIds = (member) =>
  member?.participantIds?.length
    ? member.participantIds
    : (member?.participants || [])
        .map((participant) => participant?.memberId || participant?.userId || participant?._id || "")
        .filter(Boolean);

const formatLastContact = (value) => {
  if (!value) return "Never contacted";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never contacted";

  return `Last connected: ${date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })}`;
};

const getMemberSubtext = (member) => {
  if (member?.isNotConnected) {
    const daysText = member.daysSinceContact == null ? "" : ` (${member.daysSinceContact} days ago)`;
    return `${formatLastContact(member.lastContactedAt)}${daysText}`;
  }

  return member.region || member.designation || "NEC Member";
};

const getMemberDisplayName = (member, committeeGroups = []) => {
  const isGroupSummary =
    member?.isGroupCall || String(member?.memberId || "").startsWith("group:");
  const rawName = member?.groupLabel || member?.memberName || "NEC Member";

  if (!isGroupSummary || /^All(\s|$)/i.test(rawName)) {
    return rawName;
  }

  const participantIds = getParticipantIds(member);
  const exactCommitteeGroup = committeeGroups.find((group) =>
    sameMemberSet(participantIds, group.memberIds)
  );

  if (exactCommitteeGroup) {
    return exactCommitteeGroup.title;
  }

  const allCommitteeMemberIds = committeeGroups.flatMap((group) => group.memberIds || []);
  if (sameMemberSet(participantIds, allCommitteeMemberIds)) {
    return "All Members";
  }

  const participantCount = getGroupParticipantCount(member);
  const listedNamesCount = rawName.split(",").filter((name) => name.trim()).length;
  const sectionLabels = Array.from(
    new Set((member?.participants || []).map(getMemberSectionLabel).filter(Boolean))
  );

  if (sectionLabels.length === 1 && participantCount >= 6) {
    return `All ${sectionLabels[0]}`;
  }

  if (participantCount >= 20 || listedNamesCount >= 20) {
    return "All Members";
  }

  return rawName;
};

const CountChip = ({ value, sx }) => (
  <Chip
    size="small"
    label={formatPaddedCount(value)}
    sx={{ height: 20, fontWeight: 900, minWidth: 28, ...sx }}
  />
);

const NecFollowUpTable = ({ members = [], loading = false, committeeGroups = [], onViewDetails }) => {
  const rows = useMemo(
    () =>
      members.map((member, index) => {
        const memberDisplayName = getMemberDisplayName(member, committeeGroups);

        return {
          ...member,
          _tableKey: member.memberId ||  `${memberDisplayName}-${index}`,
          _memberDisplayName: memberDisplayName,
          _memberSubtext: getMemberSubtext(member),
          _avatarIndex: index,
        };
      }),
    [members, committeeGroups],
  );

  const columns = useMemo(
    () => [
      {
        field: "_memberDisplayName",
        headerName: "NEC Member",
        minWidth: 230,
        searchKey: (row) => `${row._memberDisplayName || ""} ${row._memberSubtext || ""}`,
        renderCell: (row) => {
          const avatarBg = ["#fed7aa", "#dbeafe", "#f3e8ff", "#dcfce7"][row._avatarIndex % 4];
          const avatarColor = ["#ea580c", "#2563eb", "#9333ea", "#3ea316"][row._avatarIndex % 4];
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.4 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: avatarBg,
                  color: avatarColor,
                  fontSize: 12,
                  fontWeight: 900,
                  flexShrink: 0,
                }}
              >
                {String(row._memberDisplayName || "NM")
                  .split(" ")
                  .map((part) => part[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                {/* <Typography fontWeight={900} sx={{ color: "#111827", fontSize: 14 }}>
                  {row._memberDisplayName}
                </Typography> */}
                <Typography fontWeight={900} sx={{ color: "#111827", fontSize: 14 }}>
                  {row.isGroupCall
                    ? (() => {
                        const names = (row.memberName || "").split(",")
                          .map((name) => name.trim())
                          .filter(Boolean);
                        if (names.length <= 1) return names[0] || "NEC Member";
                        return `${names[0]} + ${names.length - 1} others`;
                      })()
                    : row._memberDisplayName}
                </Typography>
                <Typography variant="caption" sx={{ color: "#6b7280" }}>
                  {row._memberSubtext}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        field: "totalFollowUps",
        headerName: "Total",
        minWidth: 60,
        disableSearch: true,
        renderCell: (row) => (
          <Typography sx={{ fontWeight: 900, color: "#a44900" }}>
            {formatCount(row.totalFollowUps)}
          </Typography>
        ),
      },
      {
        field: "outgoingCalls",
        headerName: "Outgoing",
        minWidth: 60,
        disableSearch: true,
        renderCell: (row) => (
          <Typography sx={{ fontWeight: 800, color: "#2563eb" }}>
            {formatCount(row.outgoingCalls)}
          </Typography>
        ),
      },
      {
        field: "incomingCalls",
        headerName: "Incoming",
        minWidth: 60,
        disableSearch: true,
        renderCell: (row) => (
          <Typography sx={{ fontWeight: 800, color: "#9333ea" }}>
            {formatCount(row.incomingCalls)}
          </Typography>
        ),
      },
      // {
      //   field: "groupCalls",
      //   headerName: "Group Call",
      //   minWidth: 60,
      //   disableSearch: true,
      //   renderCell: (row) => (
      //     <Typography sx={{ fontWeight: 800, color: "#b91c1c" }}>
      //       {formatCount(row.groupCalls)}
      //     </Typography>
      //   ),
      // },
      {
        field: "queries",
        headerName: "Queries",
        minWidth: 60,
        disableSearch: true,
        renderCell: (row) => (
          <Typography sx={{ fontWeight: 800, color: "#0891b2" }}>
            {formatCount(row.queries)}
          </Typography>
        ),
      },
      {
        field: "completed",
        headerName: "Completed",
        minWidth: 60,
        disableSearch: true,
        renderCell: (row) => (
          <CountChip value={row.completed} sx={{ bgcolor: "#dcfce7", color: "#16a34a" }} />
        ),
      },
      {
        field: "pending",
        headerName: "Pending",
        minWidth: 60,
        disableSearch: true,
        renderCell: (row) => (
          <CountChip
            value={row.pending}
            sx={{
              bgcolor: (row.pending || 0) === 0 ? "#fee2e2" : "#fef3c7",
              color: (row.pending || 0) === 0 ? "#ef4444" : "#f59e0b",
            }}
          />
        ),
      },
      {
        field: "onHold",
        headerName: "On Hold",
        minWidth: 60,
        disableSearch: true,
        renderCell: (row) => (
          <CountChip
            value={row.onHold}
            sx={{
              bgcolor: (row.onHold || 0) === 0 ? "#fee2e2" : "#fef3c7",
              color: (row.onHold || 0) === 0 ? "#ef4444" : "#f59e0b",
            }}
          />
        ),
      },
      {
        field: "na",
        headerName: "N/A",
        minWidth: 60,
        disableSearch: true,
        renderCell: (row) => (
          <CountChip value={row.na} sx={{ bgcolor: "#f1f5f9", color: "#64748b" }} />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 80,
        disableSort: true,
        disableSearch: true,
        renderCell: (row) => (
          <Button
            size="small"
            variant="text"
            onClick={() => onViewDetails?.(row)}
            sx={{
              color: "#a44900",
              fontWeight: 900,
              textTransform: "none",
              whiteSpace: "nowrap",
              minWidth: 0,
              px: 0.5,
              border: "1px solid #a44900",
              "&:hover": {
                bgcolor: "#a44900",
                color: "#fff",
                border: "1px solid #a44900",
              },
            }}
            aria-label={`View ${row._memberDisplayName} details`}
          >
            View Details
          </Button>
        ),
      },
    ],
    [onViewDetails],
  );

  return (
    <ReactTable
      columns={columns}
      rows={rows}
      loading={loading}
      rowKey="_tableKey"
      initialRowsPerPage={10}
      initialOrderBy="_memberDisplayName"
    />
  );
};

export default NecFollowUpTable;